// Lo que el onboarding de marca lee y escribe en Supabase
// (ADR-2026-09-28-onboarding-v2-etapa-1): la empresa, la marca que dejó el escaneo, la
// confirmación y las respuestas de la espera.

import { supabase } from '../supabase';
import { createCompanyForUser, postOnboardingSetup } from '../createCompany';
import { MARCA_POR_DEFECTO } from '../altaDesdeCuestionario';
import { nombreDelUsuario } from '../nombreUsuario';
import { paisDelBrowser } from '../paisDelBrowser';
import { RUBRO_SIN_DEFINIR } from '../onboardingPendiente';
import { cuestionarioCompletado, marcarOnboardingPendiente } from '../cuestionarioObligatorio';
import { RUBRO_POR_CATEGORIA, SETUP_ROLES } from '../../components/onboarding/accountSetup';
import { saveMemory } from '../../pages/v2/agentMemoryApi';
import type { ColorDeMarca, TipografiasDeMarca } from './escaneo';
import { enGuion, MARCA_DEL_GUION, marcaDelGuion } from './guion';
import { memoriaDeCanal } from './canales';
import { respuestasDe } from './respuestas';

/** Los rubros que puede tener una marca, en el orden del cuestionario. */
export const RUBROS: readonly string[] = Array.from(new Set(Object.values(RUBRO_POR_CATEGORIA)));

export interface MarcaGuardada {
  id: string;
  nombre: string;
  rubro: string;
  colores: ColorDeMarca[];
  tipografias: TipografiasDeMarca;
  logoUrl: string | null;
  analisis: string;
}

/**
 * La empresa de la persona, creándola si no tiene. Se crea con el rubro CENTINELA: así,
 * si abandona a la mitad, la app la devuelve al onboarding (el mismo mecanismo del alta
 * por link, ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues). El rubro
 * real se escribe al confirmar la marca. Idempotente: el backend no crea una segunda.
 */
export async function prepararEmpresa(): Promise<{ ok: true; companyId: string } | { ok: false; error: string }> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: 'Se cerró la sesión. Inicia sesión de nuevo.' };

  const { data: perfil, error: errPerfil } = await supabase
    .from('profiles')
    .select('id_company')
    .eq('id', user.id)
    .maybeSingle();
  // Sin poder leer el perfil no se sabe si ya tiene empresa: crear "por las dudas" pisaba el
  // rubro de una empresa existente con el centinela (y la devolvía al onboarding) y el nombre
  // y el país del perfil (ADR-2026-10-12-el-onboarding-de-marca-tras-prenderlo).
  if (errPerfil) return { ok: false, error: 'No pudimos cargar tu cuenta. Revisa tu conexión e intenta de nuevo.' };
  const existente = (perfil as { id_company?: string | null } | null)?.id_company;
  if (existente) return { ok: true, companyId: existente };

  const res = await createCompanyForUser({
    userId: user.id,
    name: nombreDelUsuario(user),
    pais: paisDelBrowser() ?? 'Otro',
    companyName: MARCA_POR_DEFECTO,
    industry: RUBRO_SIN_DEFINIR,
  });
  if (!res.ok || !res.companyId) {
    return { ok: false, error: res.error || 'No pudimos crear tu cuenta. Intenta de nuevo.' };
  }
  marcarOnboardingPendiente('cuestionario');
  return { ok: true, companyId: res.companyId };
}

export async function leerMarca(brandId: string): Promise<MarcaGuardada | null> {
  const r = await buscarMarca(brandId);
  return r.estado === 'ok' ? r.marca : null;
}

/**
 * La marca, distinguiendo "no existe para esta sesión" de "no se pudo consultar". La primera
 * dice que el onboarding guardado no sirve —la marca se borró, o es de OTRA cuenta y RLS no la
 * deja ver— y hay que arrancar de cero; la segunda es la red, y el progreso no se toca
 * (review Greptile #164, ADR-2026-10-07-una-marca-que-no-existe-reinicia-el-onboarding).
 */
export async function buscarMarca(
  brandId: string,
): Promise<{ estado: 'ok'; marca: MarcaGuardada } | { estado: 'no_existe' } | { estado: 'error' }> {
  if (brandId === MARCA_DEL_GUION && enGuion()) return { estado: 'ok', marca: marcaDelGuion() };
  const { data, error } = await supabase
    .from('brand_profiles')
    .select('id, name, rubro, colors, fonts, logo_url, context')
    .eq('id', brandId)
    .maybeSingle();
  if (error) return { estado: 'error' };
  if (!data) return { estado: 'no_existe' };
  const r = data as {
    id: string;
    name: string | null;
    rubro: string | null;
    colors: unknown;
    fonts: unknown;
    logo_url: string | null;
    context: string | null;
  };
  return {
    estado: 'ok',
    marca: {
      id: r.id,
      nombre: r.name ?? '',
      rubro: r.rubro && r.rubro !== RUBRO_SIN_DEFINIR ? r.rubro : '',
      colores: Array.isArray(r.colors) ? (r.colors as ColorDeMarca[]).filter((c) => typeof c?.hex === 'string') : [],
      tipografias: r.fonts && typeof r.fonts === 'object' ? (r.fonts as TipografiasDeMarca) : {},
      logoUrl: r.logo_url || null,
      analisis: r.context ?? '',
    },
  };
}

/** Sube un logo elegido por la persona. Mismo lugar que Configuración → Mi marca. */
export async function subirLogo(file: File): Promise<string> {
  const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
  const path = `logos/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('system').upload(path, file);
  if (error) throw error;
  return supabase.storage.from('system').getPublicUrl(path).data.publicUrl;
}

export interface RespuestasDeLaEspera {
  /** Id de `SETUP_CONTENT_TYPES`, `anuncios` u `otro`. */
  primero?: string;
  /** Id de `SETUP_ROLES` u `otro`. */
  rol?: string;
  anunciosPagos?: 'no' | 'meta' | 'meta_google' | 'otro';
  /** Por dónde vende (ADR-2026-09-29-onboarding-v2-canal-de-venta): un `CanalDeVenta`. */
  canal?: string;
  /**
   * Lo que escribió la persona en "Otro" (ADR-2026-09-29-onboarding-v2-preguntas-con-otro).
   * La respuesta queda en `otro` y sólo se guarda con texto: para seguir alcanza con eso.
   */
  otros?: Partial<Record<'primero' | 'rol' | 'anunciosPagos' | 'canal', string>>;
  /**
   * Todo lo que marcó en cada pregunta, en el orden en que lo marcó
   * (ADR-2026-10-02-onboarding-v2-preguntas-de-varias). Los campos de arriba guardan la
   * principal (la primera); para leer la lista, `respuestasDe`.
   */
  todas?: Partial<Record<'primero' | 'rol' | 'anunciosPagos' | 'canal', string[]>>;
}

export const RESPUESTA_OTRO = 'otro';

/** "a", "a y b", "a, b y c". */
function enLista(partes: string[]): string {
  return partes.length <= 1 ? (partes[0] ?? '') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

const ANUNCIOS_PAGOS_TEXTO: Record<Exclude<NonNullable<RespuestasDeLaEspera['anunciosPagos']>, 'otro'>, string> = {
  no: 'Todavía no hace anuncios pagos.',
  meta: 'Hace anuncios pagos en Meta.',
  meta_google: 'Hace anuncios pagos en Meta y en Google.',
};

/**
 * "Todo bien, sigamos": la marca queda como la dejó la persona y la empresa sale del
 * onboarding pendiente. El rubro se escribe de forma AUTORITATIVA (sin él, la app la
 * devuelve acá en cada entrada); las respuestas de la espera son best-effort.
 */
export async function confirmarMarca(p: {
  companyId: string;
  marca: Pick<MarcaGuardada, 'id' | 'nombre' | 'rubro' | 'colores' | 'logoUrl'>;
  respuestas: RespuestasDeLaEspera;
  /** Con "Otro" (o un rubro que se ajustó desde "Otro"): cuál es, en sus palabras
   *  (ADR-2026-10-12-el-rubro-otro-se-cuenta-y-se-ajusta). */
  rubroDetalle?: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  // En modo guion no se escribe nada: la cuenta es de verdad.
  if (p.marca.id === MARCA_DEL_GUION && enGuion()) return { ok: true };
  const nombre = p.marca.nombre.trim() || MARCA_POR_DEFECTO;
  const rubro = p.marca.rubro || 'Otro';

  const { error: errMarca } = await supabase
    .from('brand_profiles')
    .update({ name: nombre, rubro, colors: p.marca.colores, logo_url: p.marca.logoUrl, is_draft: false })
    .eq('id', p.marca.id);
  if (errMarca) return { ok: false, error: 'No pudimos guardar tu marca. Intenta de nuevo.' };

  // Brevo se entera del rubro por `/v1/onboarding/setup`: con la empresa ya creada, esa ruta
  // sólo encola la segmentación, y SÓLO si en la base todavía está el centinela — por eso va
  // ANTES del update del rubro. Sin esto, ningún alta del onboarding de marca (ni las del
  // pago directo) llegaba a Brevo con su rubro. Best-effort: no frena el onboarding
  // (ADR-2026-10-12-el-onboarding-de-marca-tras-prenderlo).
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    // Con "Otro", lo que escribió: el backend lo clasifica para Brevo, y "Otro" solo no le dice nada.
    const industria = rubro === 'Otro' && p.rubroDetalle?.trim() ? p.rubroDetalle.trim() : rubro;
    if (user) await postOnboardingSetup({ company_name: nombre, industry: industria, user_id: user.id });
  } catch {
    // La segmentación se pierde; el alta sigue.
  }

  const { error: errEmpresa } = await supabase
    .from('company_profiles')
    .update({ rubro, nombre, ...(p.marca.logoUrl ? { logo_url: p.marca.logoUrl } : {}) })
    .eq('id_company', p.companyId);
  if (errEmpresa) return { ok: false, error: 'No pudimos guardar tu rubro. Intenta de nuevo.' };
  cuestionarioCompletado();

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const otros = p.respuestas.otros ?? {};
    // El rubro en sus palabras: el agente lo usa aunque la empresa quede en "Otro".
    if (p.rubroDetalle?.trim()) {
      await saveMemory(p.companyId, `Describió su rubro como: ${p.rubroDetalle.trim()}.`, 'rubro');
    }
    // "Otro" en el rol va tal cual, como ya hace el WelcomeModal con el rol a mano.
    const rol = p.respuestas.rol === RESPUESTA_OTRO ? otros.rol?.trim() : p.respuestas.rol;
    if (user && rol) {
      await supabase.from('profiles').update({ rol_en_empresa: rol }).eq('id', user.id);
    }
    // Con varios roles, la columna guarda el principal y el agente se entera de todos.
    const roles = respuestasDe(p.respuestas, 'rol');
    if (roles.length > 1) {
      const nombres = roles
        .map((r) => (r === RESPUESTA_OTRO ? otros.rol?.trim() : SETUP_ROLES.find((x) => x.id === r)?.label))
        .filter((x): x is string => Boolean(x));
      if (nombres.length) await saveMemory(p.companyId, `En la marca es ${enLista(nombres)}.`, 'rol');
    }
    const primeros = respuestasDe(p.respuestas, 'primero');
    if (primeros.length) {
      await supabase.from('company_profiles').update({ tipos_contenido: primeros }).eq('id_company', p.companyId);
    }
    if (primeros.includes(RESPUESTA_OTRO) && otros.primero?.trim()) {
      await saveMemory(p.companyId, `Lo primero que quiere hacer en Atlas: ${otros.primero.trim()}.`, 'primero_en_atlas');
    }
    const pagos = respuestasDe(p.respuestas, 'anunciosPagos')
      .map((id) =>
        id === RESPUESTA_OTRO
          ? otros.anunciosPagos?.trim()
            ? `Sobre anuncios pagos, contó: ${otros.anunciosPagos.trim()}.`
            : null
          : (ANUNCIOS_PAGOS_TEXTO[id as keyof typeof ANUNCIOS_PAGOS_TEXTO] ?? null),
      )
      .filter((x): x is string => Boolean(x));
    if (pagos.length) await saveMemory(p.companyId, pagos.join(' '), 'anuncios_pagos');
    if (p.respuestas.canal) {
      // Aparte del resto: si la columna todavía no existe, el error queda acá y no se lleva
      // puesto lo demás (PostgREST devuelve el error, no tira). La columna guarda la principal.
      await supabase
        .from('company_profiles')
        .update({ canal_de_venta: p.respuestas.canal })
        .eq('id_company', p.companyId);
      const memoria = respuestasDe(p.respuestas, 'canal')
        .map((c) => memoriaDeCanal(c, otros.canal))
        .filter((x): x is string => Boolean(x))
        .join(' ');
      if (memoria) await saveMemory(p.companyId, memoria, 'tienda');
    }
  } catch {
    /* best-effort: un dato de segmentación faltante no frena el onboarding */
  }
  return { ok: true };
}
